/**
 * render_display_elements/display_selection_elements.jsx
 *
 * 涵蓋選擇類型的展示元件：
 * dropdown, radio_button, checkbox, multi_select_list, switch, color_picker
 */

import React from "react"
import { Checkbox } from "../../ui/checkbox"
import { Radio, Select, Switch, ColorPicker } from "../../ui/ot"
import { FieldWrapper, ElementShell } from "../canvas_field_wrapper"

/** 共用：從 form_form_column_data_first 取得靜態選項 */
const getStaticOptions = (col) => {
    return (col?.form_column_selection_option_data || []).map(opt => {
        const cst = (opt?.selection_option_classify_structure_type_data || []).find(_ => true)
        const tw = (cst?.name_language_data || []).find(n => n.language_culture_code === "zh-tw")
        return { label: tw?.language_culture_value ?? "", value: opt?.selection_option_id }
    })
}

/** dropdown */
export const DropdownElement = ({ element, form_form_column_data_first, finalOptions, isFillMode }) => {
    const opts = finalOptions?.length ? finalOptions : getStaticOptions(form_form_column_data_first)
    return (
        <ElementShell isFillMode={isFillMode}>
            <FieldWrapper
                label={form_form_column_data_first?.code}
                required={form_form_column_data_first?.is_required === "1"}
                helpText={form_form_column_data_first?.help_text}
            >
                <Select
                    className="w-full"
                    onChange={() => {}}
                    options={opts}
                    value={element?.content ?? undefined}
                />
            </FieldWrapper>
        </ElementShell>
    )
}

/** multi_select_list */
export const MultiSelectListElement = ({ element, form_form_column_data_first, finalOptions, isFillMode }) => {
    const opts = finalOptions?.length ? finalOptions : getStaticOptions(form_form_column_data_first)
    return (
        <ElementShell isFillMode={isFillMode}>
            <FieldWrapper
                label={form_form_column_data_first?.code}
                required={form_form_column_data_first?.is_required === "1"}
                helpText={form_form_column_data_first?.help_text}
            >
                <Select
                    className="w-full"
                    onChange={() => {}}
                    mode="multiple"
                    options={opts}
                    value={Array.isArray(element?.content) ? element.content : undefined}
                />
            </FieldWrapper>
        </ElementShell>
    )
}

/** radio_button */
export const RadioButtonElement = ({ element, form_form_column_data_first, finalOptions, isFillMode }) => {
    const opts = finalOptions?.length
        ? finalOptions
        : getStaticOptions(form_form_column_data_first)
    const displayOpts = opts.length > 0 ? opts : [{ label: "選項1", value: "option1" }, { label: "選項2", value: "option2" }]

    return (
        <ElementShell isFillMode={isFillMode}>
            <FieldWrapper
                label={form_form_column_data_first?.code}
                required={form_form_column_data_first?.is_required === "1"}
                helpText={form_form_column_data_first?.help_text}
            >
                <Radio.Group
                    value={element?.content ?? undefined}
                    className="flex flex-wrap gap-x-4 gap-y-2"
                >
                    {displayOpts.map(opt => (
                        <Radio key={opt.value} value={opt.value}>
                            {opt.label}
                        </Radio>
                    ))}
                </Radio.Group>
            </FieldWrapper>
        </ElementShell>
    )
}

/** checkbox */
export const CheckboxElement = ({ element, form_form_column_data_first, finalOptions, isFillMode }) => {
    const opts = finalOptions?.length ? finalOptions : getStaticOptions(form_form_column_data_first)
    const checkedValues = Array.isArray(element?.content) ? element.content : []

    if (opts.length > 0) {
        return (
            <ElementShell isFillMode={isFillMode}>
                <FieldWrapper
                    label={form_form_column_data_first?.code}
                    required={form_form_column_data_first?.is_required === "1"}
                    helpText={form_form_column_data_first?.help_text}
                >
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                        {opts.map(opt => (
                            <label key={opt.value} className="flex items-center gap-2 cursor-pointer select-none">
                                <Checkbox checked={checkedValues.includes(opt.value)} disabled />
                                <span className="text-sm text-gray-700">{opt.label}</span>
                            </label>
                        ))}
                    </div>
                </FieldWrapper>
            </ElementShell>
        )
    }

    return (
        <ElementShell isFillMode={isFillMode}>
            <FieldWrapper
                label={form_form_column_data_first?.code}
                required={form_form_column_data_first?.is_required === "1"}
                helpText={form_form_column_data_first?.help_text}
            >
                <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={!!element?.content} disabled />
                    <span className="text-sm text-gray-700">
                        {form_form_column_data_first?.placeholder || form_form_column_data_first?.code}
                    </span>
                </label>
            </FieldWrapper>
        </ElementShell>
    )
}

/** switch */
export const SwitchElement = ({ element, form_form_column_data_first, isFillMode }) => (
    <ElementShell isFillMode={isFillMode}>
        <FieldWrapper
            label={form_form_column_data_first?.code}
            required={form_form_column_data_first?.is_required === "1"}
            helpText={form_form_column_data_first?.help_text}
        >
            <Switch mode="number" value={!!element?.content} disabled />
        </FieldWrapper>
    </ElementShell>
)

/** color_picker */
export const ColorPickerElement = ({ element, form_form_column_data_first, isFillMode }) => (
    <ElementShell isFillMode={isFillMode}>
        <FieldWrapper
            label={form_form_column_data_first?.code}
            helpText={form_form_column_data_first?.help_text}
        >
            <div className="flex items-center gap-2">
                <div
                    className="w-8 h-8 rounded border border-gray-300 flex-shrink-0"
                    style={{ background: element?.content || "#ffffff" }}
                />
                <span className="text-sm text-gray-500">{element?.content || "#ffffff"}</span>
            </div>
        </FieldWrapper>
    </ElementShell>
)
